<template>
    <v-row class="mt-0 mb-0">
        <v-col cols="12" lg="6" xl="7" class="pt-0 pb-0" v-if="mailTemplates?.length > 1">
            <v-select-filled
                persistent
                label="Modèle de mail"
                v-model="selectedTemplateName"
                :items="mailTemplates"
                item-title="title"
                class="pb-4"
                data-cy="InvoiceMailTemplatesSetting-mailTemplate"
                ref="mailTemplateSelect"
            >
                <template v-slot:item="{ item }">
                    <v-list-item @click="onTemplateChange(item.value)">
                        <v-row align="center" no-gutters>
                            <v-col data-cy="InvoiceMailTemplatesSetting-template" cols="8">
                                <span>{{ item.title }}</span>
                            </v-col>
                            <v-col cols="4" class="text-right">
                                <v-btn-trash
                                    v-if="item.value !== DEFAULT_TEMPLATE_NAME"
                                    data-cy="InvoiceMailTemplatesSetting-delete"
                                    color="red"
                                    x-small
                                    @click.stop="openDeleteMailTemplateDialog(item?.value)"
                                />
                            </v-col>
                        </v-row>
                    </v-list-item>
                </template>
            </v-select-filled>
        </v-col>
        <v-col cols="12" lg="6" xl="5" class="pt-0 pb-4 d-flex align-center">
            <v-btn-primary
                block
                size="default"
                @click="showAddTemplateModal"
                data-cy="InvoiceMailTemplatesSetting-addNewMailTemplateBtn"
            >
                Ajouter un nouveau modèle
            </v-btn-primary>
        </v-col>
    </v-row>
    <oss-dialog
        v-model="deleteDialog"
        title="Suppression du modèle de mail"
        width="550px"
        @valid="confirmDeleteTemplate"
        @close="closeDeleteMailTemplateDialog"
    >
        <h4 class="text-center">
            &#xCA;tes-vous sûr(e) de vouloir supprimer ce modèle de mail ? Cette action est
            irréversible
        </h4>
    </oss-dialog>
    <oss-dialog
        v-model="dialog"
        minWidth="30vw"
        title="Ajouter un nouveau modèle"
        @close="closeAddTemplateModal"
        @valid="addNewMailTemplate"
        prefixDataCy="InvoiceMailTemplatesSetting-addNewTempateModal"
    >
        <v-text-field-filled
            data-cy="InvoiceMailTemplatesSetting-newTemplateName"
            label="Nom du modèle"
            v-model="templateTitle"
            required
            :error-messages="errorMessage"
        ></v-text-field-filled>
    </oss-dialog>
</template>

<script lang="ts">
import { MailTemplate } from "@/store/store";
import { defaultInvoiceMail } from "@/types/invoiceAdvanceSettings.types";
import invoiceDefaultMailScheme from "@/assets/json/invoiceDefaultMailScheme.json";
import { Component, Vue, Prop, toNative, Watch } from "vue-facing-decorator";

@Component({
    emits: ["templateDelete"],
})
export class InvoiceMailTemplatesSetting extends Vue {
    @Prop() defaultMails: defaultInvoiceMail;

    dialog: boolean = false;
    templateTitle: string = "";
    errorMessage: string = "";

    deleteDialog: boolean = false;
    templateNameToDelete: string = "";

    get DEFAULT_TEMPLATE_NAME() {
        return "default";
    }
    // Computed function: Fetch mail templates
    get mailTemplates(): MailTemplate[] {
        return this.$store.state.invoiceMailTemplates;
    }

    // Computed function: Generate a valid template name by replacing spaces with underscores
    get templateName(): string {
        return this.templateTitle.replaceAll(" ", "_").toLowerCase();
    }

    // Fetch selectedTemplateName from the store
    get selectedTemplateName(): string {
        return this.$store.state.selectedInvoiceMailTemplateName;
    }

    // Update selectedTemplateName in the store
    set selectedTemplateName(value: string) {
        this.$store.dispatch("updateSelectedTemplateName", value);
    }

    set deleteTemplateMail(value: string) {
        this.$store.dispatch("deleteInvoiceMailTemplate", value);
    }

    // Watch pour détecter les changements de mailTemplates
    @Watch("mailTemplates", { deep: true })
    onMailTemplatesUpdated(newTemplates: MailTemplate[]) {
        // Vérifier si le modèle sélectionné existe encore
        if (
            !newTemplates.some((t) => t.value === this.$store.state.selectedInvoiceMailTemplateName)
        ) {
            this.$store.dispatch("updateSelectedTemplateName", this.DEFAULT_TEMPLATE_NAME);
            this.loadTemplateContent(this.DEFAULT_TEMPLATE_NAME);
        }
    }

    // Lifecycle Hook: Initialize default template when the component is mounted
    async mounted() {
        if (this.mailTemplates.length === 0) {
            await this.initializeDefaultTemplate();
        } else {
            this.loadTemplateContent(this.selectedTemplateName);
        }
    }

    // Method: Initialize the default template if no templates exist
    async initializeDefaultTemplate() {
        const defaultTemplate: MailTemplate = {
            title: "Par défaut",
            value: this.DEFAULT_TEMPLATE_NAME,
            header: this.defaultMails.invoice.header || invoiceDefaultMailScheme.invoice.header,
            body: this.defaultMails.invoice.body || invoiceDefaultMailScheme.invoice.body,
            signature: this.defaultMails.signature || "$COMPANY_NAME\n$USER_NAME",
        };
        await this.$store.dispatch("initializeInvoiceMailTemplates", defaultTemplate);
    }

    // Method: Handle template selection change
    onTemplateChange(templateName: string) {
        this.loadTemplateContent(templateName);
        this.$refs.mailTemplateSelect.blur();
    }

    openDeleteMailTemplateDialog(mailTemaplteValue: string) {
        this.templateNameToDelete = mailTemaplteValue;
        this.deleteDialog = true;
    }

    closeDeleteMailTemplateDialog() {
        this.deleteDialog = false;
    }

    // Open modal
    showAddTemplateModal() {
        this.dialog = true;
        this.templateTitle = "";
        this.errorMessage = "";
    }

    // Close modal
    closeAddTemplateModal() {
        this.dialog = false;
    }

    // Method: Load the content of the selected template
    loadTemplateContent(templateName: string) {
        const selectedTemplate = this.mailTemplates.find(
            (template) => template.value === templateName,
        );
        if (!selectedTemplate) {
            console.error("Template not found:", templateName);
            return;
        }

        // Update the selected template and default mail content
        this.selectedTemplateName = selectedTemplate.value;
        this.defaultMails.invoice.header = selectedTemplate.header;
        this.defaultMails.invoice.body = selectedTemplate.body;
        this.defaultMails.signature = selectedTemplate.signature;
    }

    async addNewMailTemplate() {
        // Field validation
        if (!this.templateTitle) {
            this.errorMessage = "Le nom du modèle est obligatoire";
            return;
        }

        const isTemplateExists = this.mailTemplates.some(
            (template) => template.value === this.templateName,
        );

        if (isTemplateExists) {
            this.errorMessage = "Un modèle avec ce nom existe déjà";
            return;
        }

        const newMailTemplate: MailTemplate = {
            title: this.templateTitle,
            value: this.templateName.trim(),
            header: this.defaultMails.invoice.header,
            body: this.defaultMails.invoice.body,
            signature: this.defaultMails.signature,
        };

        try {
            // Dispatch the action to add the new template
            await this.$store.dispatch("addInvoiceMailTemplate", newMailTemplate);

            // Update the selected template and close the modal
            this.selectedTemplateName = this.templateName;
            this.closeAddTemplateModal();
        } catch (error) {
            console.error("Failed to add new template:", error);
        }
    }

    confirmDeleteTemplate() {
        this.$store.dispatch("deleteInvoiceMailTemplate", this.templateNameToDelete);
        this.onTemplateChange(this.DEFAULT_TEMPLATE_NAME);
        this.closeDeleteMailTemplateDialog();
        this.$emit("templateDelete");
    }
}

export default toNative(InvoiceMailTemplatesSetting);
</script>
